import type { LocalizedDict } from '../index'

// Shared UI building blocks used across many screens: dialog, share-link
// modal, credential lock, file lifecycle controls, rich text editor toolbar,
// signature pad, bulk tag dropdown, sidebar, auth hero editor and the root
// shell. One section per component.
export type SharedUiStrings = {
  dialog: {
    close: string
  }
  shareLink: {
    title: string
    close: string
    qrAlt: string
    generatingQr: string
    copied: string
    copy: string
    expires: (date: string) => string
    uses: (count: number) => string
    expiryTail: string
  }
  credentialLock: {
    notConfigured: string
    connected: string
    defaultLabel: string
    savedPrefix: (label: string) => string
    savedSuffix: string
    replaceConfirm: string
    yesReplace: string
    cancel: string
    editCredentials: string
  }
  fileLifecycle: {
    purposes: {
      customer_upload: string
      design_source: string
      mockup: string
      approved_mockup: string
      production_final: string
      vector_output: string
      ai_composed: string
    }
    failed: string
    purposeTitle: string
    purposePlaceholder: string
    lockedTitle: string
    locked: string
    approveTitle: string
    approve: string
    lockTitle: string
    lock: string
    publicLinkTitle: string
    publicLink: string
  }
  richText: {
    linkPrompt: string
    paragraph: string
    heading2: string
    heading3: string
    bold: string
    italic: string
    underline: string
    alignLeft: string
    alignCenter: string
    alignRight: string
    bulletList: string
    numberedList: string
    link: string
  }
  signaturePad: {
    defaultAriaLabel: string
    hint: string
    clear: string
  }
  bulkTag: {
    addTag: string
    tag: string
    selectOrdersFirst: string
    tagSelected: (count: number) => string
    manageTags: string
    applyTag: string
    adminNoSelection: string
    sellerNoSelection: string
    loading: string
    empty: string
  }
  sidebar: {
    primaryNavigation: string
    closeNavigation: string
    collapseNavigation: string
    expandNavigation: string
    showContextSelector: string
    collapse: string
    expand: string
    collapseItem: (label: string) => string
    expandItem: (label: string) => string
    comingSoon: string
    soon: string
    unread: (count: number) => string
  }
  authHero: {
    surfaces: { app: string; admin: string }
    bundledDefault: string
    publishedCustom: string
    focusHelper: (x: number, y: number) => string
    unpublishedDraft: string
    surfaceNote: string
    replaceImage: string
    cropLabel: (helper: string) => string
    previewNote: string
    altTextEn: string
    altTextEnHint: string
    altTextTr: string
    altTextTrHint: string
    horizontalFocus: string
    verticalFocus: string
    restoring: string
    restoreDefault: string
    publishing: string
    savePublish: string
  }
  rootShell: {
    loadingDashboard: string
    accountLoadError: string
    signInAgain: string
    pickRole: string
  }
}

export const sharedUiI18n: LocalizedDict<SharedUiStrings> = {
  en: {
    dialog: {
      close: 'Close',
    },
    shareLink: {
      title: 'Public link',
      close: 'Close',
      qrAlt: 'QR code',
      generatingQr: 'Generating QR…',
      copied: 'Copied',
      copy: 'Copy',
      expires: (date) => `Expires ${date}`,
      uses: (count) => ` · ${count} use(s)`,
      expiryTail: '. Anyone with this link can download the file until then — it carries no other data.',
    },
    credentialLock: {
      notConfigured: 'Not configured',
      connected: 'Connected',
      defaultLabel: 'Credentials',
      savedPrefix: (label) => `${label} are saved`,
      savedSuffix: 'and encrypted. They stay hidden unless you replace them.',
      replaceConfirm: 'Replace saved secret?',
      yesReplace: 'Yes, replace',
      cancel: 'Cancel',
      editCredentials: 'Edit credentials',
    },
    fileLifecycle: {
      purposes: {
        customer_upload: 'Customer upload',
        design_source: 'Design source',
        mockup: 'Mockup',
        approved_mockup: 'Approved mockup',
        production_final: 'Production final',
        vector_output: 'Vector output',
        ai_composed: 'AI composed',
      },
      failed: 'Failed',
      purposeTitle: 'File purpose (lifecycle)',
      purposePlaceholder: '— purpose —',
      lockedTitle: 'Approved + locked — click to unlock for a deliberate edit',
      locked: 'Locked',
      approveTitle: 'Approve + lock (production-final finalize)',
      approve: 'Approve',
      lockTitle: 'Lock without approving',
      lock: 'Lock',
      publicLinkTitle: 'Mint a public link + QR (production-floor access)',
      publicLink: 'Public link',
    },
    richText: {
      linkPrompt: 'Link URL',
      paragraph: 'Paragraph',
      heading2: 'Heading 2',
      heading3: 'Heading 3',
      bold: 'Bold',
      italic: 'Italic',
      underline: 'Underline',
      alignLeft: 'Align left',
      alignCenter: 'Align center',
      alignRight: 'Align right',
      bulletList: 'Bullet list',
      numberedList: 'Numbered list',
      link: 'Link',
    },
    signaturePad: {
      defaultAriaLabel: 'Signature pad',
      hint: 'Sign above with your mouse or finger.',
      clear: 'Clear',
    },
    bulkTag: {
      addTag: 'Add tag',
      tag: 'Tag',
      selectOrdersFirst: 'Select orders first to apply tags',
      tagSelected: (count) => `Tag ${count} selected order(s)`,
      manageTags: 'Manage Tags',
      applyTag: 'Apply tag',
      adminNoSelection: 'Select orders first to apply tags.',
      sellerNoSelection: 'Select one or more orders first.',
      loading: 'Loading…',
      empty: 'No tags yet.',
    },
    sidebar: {
      primaryNavigation: 'Primary navigation',
      closeNavigation: 'Close navigation',
      collapseNavigation: 'Collapse navigation',
      expandNavigation: 'Expand navigation',
      showContextSelector: 'Show context selector',
      collapse: 'Collapse',
      expand: 'Expand',
      collapseItem: (label) => `Collapse ${label}`,
      expandItem: (label) => `Expand ${label}`,
      comingSoon: 'Coming soon',
      soon: 'soon',
      unread: (count) => `${count} unread`,
    },
    authHero: {
      surfaces: { app: 'App login', admin: 'Admin login' },
      bundledDefault: 'Bundled default',
      publishedCustom: 'Published custom image',
      focusHelper: (x, y) => `${x}% horizontal · ${y}% vertical`,
      unpublishedDraft: 'Unpublished draft',
      surfaceNote: 'Changes affect only this login surface.',
      replaceImage: 'Replace image',
      cropLabel: (helper) => `Real login crop · ${helper}`,
      previewNote: 'This preview uses the same object-position values as the public login page.',
      altTextEn: 'English alt text',
      altTextEnHint: 'Required for screen readers and fallback context.',
      altTextTr: 'Turkish alt text',
      altTextTrHint: 'Optional; English is used when empty.',
      horizontalFocus: 'Horizontal focus',
      verticalFocus: 'Vertical focus',
      restoring: 'Restoring…',
      restoreDefault: 'Restore default',
      publishing: 'Publishing…',
      savePublish: 'Save & publish',
    },
    rootShell: {
      loadingDashboard: 'Loading your dashboard…',
      accountLoadError: 'Could not load your account.',
      signInAgain: 'Sign in again',
      pickRole: 'Pick a role to start (Admin / Seller / Supply):',
    },
  },
  tr: {
    dialog: {
      close: 'Kapat',
    },
    shareLink: {
      title: 'Herkese açık bağlantı',
      close: 'Kapat',
      qrAlt: 'QR kodu',
      generatingQr: 'QR oluşturuluyor…',
      copied: 'Kopyalandı',
      copy: 'Kopyala',
      expires: (date) => `Son geçerlilik ${date}`,
      uses: (count) => ` · ${count} kullanım`,
      expiryTail: '. Bu bağlantıya sahip herkes o zamana kadar dosyayı indirebilir — başka hiçbir veri taşımaz.',
    },
    credentialLock: {
      notConfigured: 'Ayarlanmadı',
      connected: 'Bağlı',
      defaultLabel: 'Kimlik bilgileri',
      savedPrefix: (label) => `${label} kaydedildi`,
      savedSuffix: 've şifrelendi. Değiştirmediğin sürece gizli kalır.',
      replaceConfirm: 'Kayıtlı gizli bilgi değiştirilsin mi?',
      yesReplace: 'Evet, değiştir',
      cancel: 'Vazgeç',
      editCredentials: 'Kimlik bilgilerini düzenle',
    },
    fileLifecycle: {
      purposes: {
        customer_upload: 'Müşteri yüklemesi',
        design_source: 'Tasarım kaynağı',
        mockup: 'Mockup',
        approved_mockup: 'Onaylı mockup',
        production_final: 'Üretim son hali',
        vector_output: 'Vektör çıktı',
        ai_composed: 'Yapay zekâ ile oluşturuldu',
      },
      failed: 'Başarısız',
      purposeTitle: 'Dosya amacı (yaşam döngüsü)',
      purposePlaceholder: '— amaç —',
      lockedTitle: 'Onaylandı + kilitlendi — bilinçli düzenleme için kilidi açmak üzere tıkla',
      locked: 'Kilitli',
      approveTitle: 'Onayla + kilitle (üretim son hali)',
      approve: 'Onayla',
      lockTitle: 'Onaylamadan kilitle',
      lock: 'Kilitle',
      publicLinkTitle: 'Herkese açık bağlantı + QR oluştur (üretim alanı erişimi)',
      publicLink: 'Herkese açık bağlantı',
    },
    richText: {
      linkPrompt: 'Bağlantı adresi',
      paragraph: 'Paragraf',
      heading2: 'Başlık 2',
      heading3: 'Başlık 3',
      bold: 'Kalın',
      italic: 'İtalik',
      underline: 'Altı çizili',
      alignLeft: 'Sola hizala',
      alignCenter: 'Ortala',
      alignRight: 'Sağa hizala',
      bulletList: 'Madde listesi',
      numberedList: 'Numaralı liste',
      link: 'Bağlantı',
    },
    signaturePad: {
      defaultAriaLabel: 'İmza alanı',
      hint: 'Yukarıya fare veya parmağınla imzala.',
      clear: 'Temizle',
    },
    bulkTag: {
      addTag: 'Etiket ekle',
      tag: 'Etiket',
      selectOrdersFirst: 'Etiket eklemek için önce sipariş seç',
      tagSelected: (count) => `Seçili ${count} siparişe etiket ekle`,
      manageTags: 'Etiketleri yönet',
      applyTag: 'Etiket uygula',
      adminNoSelection: 'Etiket eklemek için önce sipariş seç.',
      sellerNoSelection: 'Önce bir veya daha fazla sipariş seç.',
      loading: 'Yükleniyor…',
      empty: 'Henüz etiket yok.',
    },
    sidebar: {
      primaryNavigation: 'Ana menü',
      closeNavigation: 'Menüyü kapat',
      collapseNavigation: 'Menüyü daralt',
      expandNavigation: 'Menüyü genişlet',
      showContextSelector: 'Bağlam seçicisini göster',
      collapse: 'Daralt',
      expand: 'Genişlet',
      collapseItem: (label) => `${label} daralt`,
      expandItem: (label) => `${label} genişlet`,
      comingSoon: 'Yakında',
      soon: 'yakında',
      unread: (count) => `${count} okunmamış`,
    },
    authHero: {
      surfaces: { app: 'Uygulama girişi', admin: 'Admin girişi' },
      bundledDefault: 'Varsayılan görsel',
      publishedCustom: 'Yayındaki özel görsel',
      focusHelper: (x, y) => `yatay %${x} · dikey %${y}`,
      unpublishedDraft: 'Yayınlanmamış taslak',
      surfaceNote: 'Değişiklikler yalnızca bu giriş ekranını etkiler.',
      replaceImage: 'Görseli değiştir',
      cropLabel: (helper) => `Gerçek giriş kırpması · ${helper}`,
      previewNote: 'Bu önizleme, herkese açık giriş sayfasıyla aynı konumlandırma değerlerini kullanır.',
      altTextEn: 'İngilizce alternatif metin',
      altTextEnHint: 'Ekran okuyucular ve görsel yüklenmezse gösterilecek metin için zorunlu.',
      altTextTr: 'Türkçe alternatif metin',
      altTextTrHint: 'İsteğe bağlı; boş bırakılırsa İngilizce kullanılır.',
      horizontalFocus: 'Yatay odak',
      verticalFocus: 'Dikey odak',
      restoring: 'Geri yükleniyor…',
      restoreDefault: 'Varsayılana dön',
      publishing: 'Yayınlanıyor…',
      savePublish: 'Kaydet ve yayınla',
    },
    rootShell: {
      loadingDashboard: 'Panelin yükleniyor…',
      accountLoadError: 'Hesabın yüklenemedi.',
      signInAgain: 'Tekrar giriş yap',
      pickRole: 'Bir rol seçerek başla (Admin / Seller / Supply):',
    },
  },
}
